import { provideAsmCoreViews } from '@asm/components-core/providers/provide-asm-core-views';
import { provideAsmTableViews } from '@asm/components-table/providers/provide-asm-table-views';
import { ApplicationConfig, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { NavigationError, provideRouter, withNavigationErrorHandler } from '@angular/router';
import {
  AuthenticationService,
  AuthorizationService,
  ChunkTools,
  provideSolAuthentication,
  provideSolComponents,
  provideSolDebug,
  provideSolHttpClient,
  provideSolLocale,
  provideSolNavigation,
  provideSolPreferences,
  provideSolStorage,
  provideSolTheme,
} from '@sol/components';
import { AsmLanguage } from '@asm/components-core/models/asm-language';
import { LicensingAuthorizationService } from './licensing-authorization.service';
import { ASM_TABLE_LAYOUT_SETTINGS } from '@asm/components-table/services/asm-table-layout-settings';
import { provideAsmComponents } from '@asm/components-core/providers/provide-asm-components';
import { environment } from '../environments/environment';
import * as packageJson from '../../package.json';
import { routes } from './app.routes';
import { apps } from './apps';
import { LicensingPreferencesService } from './licensing-preferences.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withNavigationErrorHandler((e: NavigationError) => ChunkTools.reloadOnChunkError(e.error)),
    ),
    ...provideSolHttpClient({ authentication: environment.AUTH_ENABLED, mocks: environment.HTTP_MOCKS }),
    provideSolComponents(packageJson.version, (viewService, typeService) => {
      provideAsmCoreViews(viewService, typeService);
      provideAsmTableViews(viewService, typeService);
    }),
    provideSolDebug(environment.ENV),
    ...(environment.AUTH_ENABLED
      ? provideSolAuthentication({
          url: environment.KEYCLOAK_URL,
          realm: environment.KEYCLOAK_REALM,
          clientId: environment.KEYCLOAK_CLIENT_ID,
        })
      : []),
    provideSolStorage(environment.APP),
    ...provideSolPreferences(LicensingPreferencesService),
    { provide: ASM_TABLE_LAYOUT_SETTINGS, useExisting: LicensingPreferencesService },
    provideSolTheme(environment.THEME),
    provideSolLocale(
      ['asm-licensing', 'asm-components-licensing', 'asm-components-core', 'sol-components'],
      [AsmLanguage.en],
    ),
    provideSolNavigation(apps, routes, environment),
    provideAsmComponents(
      environment.ENV,
      environment.ASM_API_URL,
      environment.LICENSE_SERVICE_QUERY_URL,
      environment.LICENSE_SERVICE_COMMAND_URL,
    ),
    provideAppInitializer(() => {
      const authentication = inject(AuthenticationService);
      const authorization = inject(AuthorizationService);
      const licensing = inject(LicensingAuthorizationService);
      licensing.watch(state => authorization.setAuthorities(state.authorities, state.fetched));
      authentication.fetch();
    }),
  ],
};
